import {
  Box,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from '@mui/material';
import React, { FC, useState } from 'react';
import { useGetTemplateIndustries } from '../hooks/useGetTemplateIndustries';
import { white } from '@styles/colors';
import TemplatePositionCard from './TemplatePositionCard';
import { Position } from '@staffedupusa/shared-components';
import TemplateJobPostingModal from './TemplateJobPostingModal';
import { toast } from 'react-toastify';
import { useEditIndustryTemplate } from '../hooks/useEditIndustryTemplate';
import { Add } from '@mui/icons-material';
import { EnterpriseTemplate } from '../types';
import { getCookie } from '@utils/getCookie';
import ConfirmationModal from './ConfirmationModal';

type TemplatePositionListProps = {
  templateData: EnterpriseTemplate;
};

const TemplatePositionList: FC<TemplatePositionListProps> = ({
  templateData,
}) => {
  const dontShowWarningPopup = getCookie('dontShowWarningPopup');
  const isAppliedChanges = templateData.apply_changes && !dontShowWarningPopup;
  const [errorText, setErrorText] = useState('');
  const [isJobPostingModalOpen, setIsJobPostingModalOpen] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmedSelection, setIsConfirmedSelection] = useState(false);
  const { industriesList } = useGetTemplateIndustries({
    setErrorText,
    enabled: !templateData?.industry,
  });

  const { editIndustry } = useEditIndustryTemplate({
    setErrorText,
  });

  const handleIndustryChange = (
    event: React.ChangeEvent<{ value: string }>,
  ) => {
    setSelectedIndustry(event.target.value as string);
  };

  const handleSubmit = () => {
    if (isAppliedChanges && !isConfirmedSelection && !isConfirmOpen) {
      setIsConfirmOpen(true);
      return;
    }
    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message);
      },
    };
    editIndustry(
      { industry_id: selectedIndustry, etemplate_id: templateData?.id },
      options,
    );
  };
  const handleConfirm = () => {
    selectedIndustry && setIsConfirmedSelection(true);
    !selectedIndustry && setIsJobPostingModalOpen(true);
    setIsConfirmOpen(false);
    selectedIndustry && handleSubmit();
  };
  const handleCancel = () => {
    setIsConfirmedSelection(false);
    setIsConfirmOpen(false);
  };

  return (
    <Stack>
      {templateData?.industry ? (
        <Stack spacing={2}>
          <Button
            variant="contained"
            color="success"
            startIcon={<Add />}
            onClick={() => {
              if (isAppliedChanges) {
                setIsConfirmOpen(true);
              } else {
                setIsJobPostingModalOpen(true);
              }
            }}
            sx={{ textAlign: 'end', alignSelf: 'end', width: 'fit-content' }}
          >
            Add Position
          </Button>
          <Stack
            spacing={3}
            bgcolor={white}
            p={3}
            borderRadius={8}
            width="100%"
          >
            {templateData?.job_listing &&
            templateData?.job_listing?.length > 0 ? (
              templateData?.job_listing
                .sort((a: Position, b: Position) =>
                  a.status === 4 ? 1 : b.status === 4 ? -1 : 0,
                )
                .map((item: Position) => {
                  return (
                    <TemplatePositionCard
                      jobPosting={item}
                      templateData={templateData}
                    />
                  );
                })
            ) : (
              <Box>No Job Lisiting</Box>
            )}
          </Stack>
          <TemplateJobPostingModal
            templateId={templateData?.id}
            isOpen={isJobPostingModalOpen}
            handleClose={() => {
              setIsJobPostingModalOpen(false);
            }}
            jobPosting={null}
            industryId={templateData.industry.id}
          />
        </Stack>
      ) : (
        <Stack
          alignItems="center"
          padding={3}
          borderRadius={5}
          spacing={2}
          sx={{ backgroundColor: white }}
        >
          <Box width="80%" alignSelf="center">
            {industriesList && industriesList?.dt_object?.data.length > 0 && (
              <FormControl fullWidth>
                <Typography id="industry-select-label">
                  Select Industry
                </Typography>
                <Select
                  value={selectedIndustry}
                  // @ts-ignore
                  onChange={(e) => handleIndustryChange(e)}
                  MenuProps={{
                    PaperProps: {
                      style: { borderRadius: 5, maxHeight: '250px' },
                    },
                  }}
                >
                  {industriesList?.dt_object?.data?.map(
                    (item: { id: number; name: string }) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
          <Button
            disabled={!selectedIndustry}
            sx={{ width: '80%' }}
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Stack>
      )}
      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Stack>
  );
};

export default TemplatePositionList;
